import { useEffect } from 'react'
import Button from './Button'
import { colors } from '../../colors'

interface GuidelinesModalProps {
  isOpen: boolean
  onClose: () => void
}

const GuidelinesModal: React.FC<GuidelinesModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  const overlayStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  }

  const modalStyles: React.CSSProperties = {
    backgroundColor: colors.white,
    borderRadius: '8px',
    padding: '16px',
    margin: '24px 14px',
    width: 'calc(100% - 28px)',
    height: 'calc(100vh - 48px)',
    maxWidth: '550px',
    maxHeight: '550px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'left',
    fontFamily: 'Hiragino Sans',
  }

  const titleStyles: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#000000',
    margin: 0,
    lineHeight: '1.5',
  }

  const contentStyles: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    border: `1px solid ${colors['border-light']}`,
    borderRadius: '4px',
    padding: '8px',
  }

  const textStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '1.6',
    color: colors['text-default'],
    margin: 0,
    whiteSpace: 'pre-line',
    fontFamily: 'Hiragino Sans',
  }

  const guidelineTitle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '1.2',
    color: colors['text-default'],
    padding: '8px 0',
    marginBottom: '10px',
    whiteSpace: 'pre-line',
    fontFamily: 'Hiragino Sans',
    borderBottom: `1px solid ${colors['border-light']}`,
  }

  const guidelineSubTitle: React.CSSProperties = {
    fontSize: '16px',
    lineHeight: '1.2',
    color: colors['text-default'],
    padding: '8px 0',
    marginBottom: '10px',
    whiteSpace: 'pre-line',
    fontFamily: 'Hiragino Sans',
  }

  const GuidelinesText = () => {
    return (
      <div style={textStyles}>
        <h4 style={guidelineTitle}>はじめに</h4>
        <p>
          「みんなの“おいしい”口コミ」は、「スーパーで買った
          “これおいしかった！”を、みんなで気軽にシェアできる」コミュニティです。
        </p>
        <p>
          毎日の買い物の中で見つけたお得な商品、思わずリピートしたくなるお惣菜、期間限定のスイーツなど、
          “ちょっと嬉しい発見”
          を紹介し合うことで、誰かの買い物がもっと楽しく、もっと便利になるきっかけをつくっていきます。
        </p>
        <p>
          「伝えたい」「教えたい」「共感したい」という気持ちを大切に、あたたかくやさしいつながりが生まれる場を一緒につくっていきましょう。
        </p>

        <h4 style={guidelineTitle}>投稿ルールとマナー</h4>
        <p>
          「みんなの“おいしい”口コミ」では、スーパーで見つけた「おいしいもの」を、ぜひ気軽に投稿してください。
        </p>
        <p>
          また、事務局から「今週のテーマ」をご案内することがあります。テーマに沿って投稿いただくことで、みなさまのおすすめがより多くの人に届きやすくなります。
        </p>

        <div>テーマの例：</div>
        <ul>
          <li>「買ってみたら、めっちゃ美味しかった」驚きのお惣菜</li>
          <li>ケーキ・プリン・どら焼き...スーパーで売ってる絶品スイーツ</li>
          <li>「おいしいから、また買っちゃう♪」お気に入りのお弁当</li>
        </ul>
        <p>最新のテーマはアプリ内でお知らせします。</p>
        <p>
          なお、投稿された内容（写真やコメントなど）は、すべて運営事務局にて確認のうえ公開されます。
          <br />
          ※詳細は、後述の「運営事務局について」をご確認ください。
        </p>

        <div>投稿の際のご注意（控えていただきたいこと）</div>
        <ul>
          <li>スーパーのオリジナル商品以外の投稿（どこのスーパーでも買える大手メーカーの商品ではなく、そのスーパーならではの商品を投稿してください）</li>
          <li>商品と関係のない画像・文章、人物の写りこんだ画像など</li>
          <li>誹謗中傷や、ネガティブな内容（「まずい」「最悪だった」など）</li>
          <li>お店やメーカーを特定してのクレーム投稿</li>
          <li>店内での撮影（トラブル防止のため、商品の写真は必ずご自宅などで撮影してください）</li>
          <li>「みんなの“おいしい”口コミ」の目的と異なる話題</li>
          <li>虚偽や事実と異なる内容</li>
          <li>同じ内容の繰り返し投稿</li>
        </ul>

        <h4 style={guidelineTitle}>投稿に関する禁止事項</h4>
        <p>
          「みんなの“おいしい”口コミ」をすべての方が安心して楽しめる場所にするため、以下の行為を禁止します。
        </p>
        <ul>
          <li>
            著作権など第三者の権利を侵害する投稿
            <ul>
              <li>例：他のサイトから無断で取得した画像・文章の投稿</li>
            </ul>
          </li>
          <li>
            個人情報や機密情報などプライバシーに関わる投稿
            <ul>
              <li>例：氏名・住所・電話番号・メールアドレス・顔がはっきり写った写真など</li>
            </ul>
          </li>
          <li>
            未成年者に悪影響を及ぼすような投稿
            <ul>
              <li>未成年者にタバコやアルコールを推奨するような投稿</li>
            </ul>
          </li>
          <li>他人になりすましての投稿、または営利目的の投稿（広告・宣伝等）</li>
          <li>わいせつ・残虐な内容の投稿</li>
          <li>出会いを求めるような内容の投稿</li>
          <li>特定の政治・宗教に関する投稿</li>
          <li>
            法令違反・犯罪行為・公序良俗・迷惑行為に結びつく投稿
            <ul>
              <li>特に景品表示法・薬機法などの法律に違反するような投稿</li>
              <li>賭博・無限連鎖講に関する投稿</li>
              <li>薬物に関する投稿</li>
            </ul>
          </li>
        </ul>
        <p>上記のほか、「くふうトクバイ 利用規約 第14条（禁止行為）」の記載もあわせてご確認ください。</p>
        <p>
          <a
            href="https://kufu.jp/terms/tokubai"
            target="_blank"
            rel="noopener noreferrer"
          >
            くふうトクバイ 利用規約
          </a>
        </p>

        <h4 style={guidelineTitle}>運営事務局について</h4>
        <h5 style={guidelineSubTitle}>投稿内容の確認と公開</h5>
        <p></p>
        <p>
          すべての投稿は、運営事務局が内容（写真やコメントなど）を確認し、公開可否を判断していますので、投稿したものが、必ずしも公開されるものとは限りません。
          <br />
          また、公開がされた投稿であっても、「みんなの“おいしい”口コミ」にふさわしくないと運営事務局が判断した場合、当該投稿を予告なく非公開・削除することがあります。
        </p>
        <p>
          みなさまに安心してご利用いただくための対応ですので、ご理解をいただきますようお願いいたします。
        </p>

        <h5 style={guidelineSubTitle}>免責事項・ガイドラインの変更について</h5>
        <ul>
          <li>
            ユーザーによる「みんなの“おいしい”口コミ」への投稿は、ご自身の責任において行っていただくものとします。
          </li>
          <li>
            投稿された内容は当社の運営事務局にて確認しますが、当社は当該確認を行う義務を負うものではありません。また、投稿内容の適法性、真実性などを保証するものではありません。
          </li>
          <li>
            「みんなの“おいしい”口コミ」の利用によってユーザーご自身や第三者に損害が生じた場合であっても、当社に故意または重過失がある場合を除き、当社は一切の責任を負いません。
          </li>
          <li>本ガイドラインは、より良い運営のために随時見直し・更新する場合があります。</li>
          <li>
            「みんなの“おいしい”口コミ」は、予告なく内容の変更または終了する場合があります。あらかじめご了承ください。
          </li>
        </ul>

        <h5 style={guidelineSubTitle}>お問い合わせ・ご要望について</h5>
        <p>
          「みんなの“おいしい”口コミ」に関するご質問やご意見がある場合は、アプリ内の「お問い合わせ」よりご連絡ください。
        </p>
      </div>
    )
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div style={overlayStyles} onClick={handleOverlayClick}>
      <div style={modalStyles}>
        <h3 style={titleStyles}>「みんなの“おいしい”口コミ」ご利用のみなさまへのお願い</h3>
        <div style={contentStyles}>
          <GuidelinesText />
        </div>
        <Button variant="secondary" onClick={onClose}>
          とじる
        </Button>
      </div>
    </div>
  )
}

export default GuidelinesModal
